import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, useTheme } from '@mui/material';
import { Receipt } from '@mui/icons-material';
import styled from '@emotion/styled';
import BaseWidget from './BaseWidget';
import { useFetchQueue } from '../hooks/useFetchQueue';
import { requestGetQuotes } from '../redux/sagas/requests/quote';
import { statusColor } from '../utils/statusColors';
import { formatDayShort, serverDayOf } from '../utils/dateUtils';

const QuoteList = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
});

const QuoteRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    '&:last-child': {
        borderBottom: 'none',
    },
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
        borderRadius: theme.shape.borderRadius,
    },
}));

const QuoteName = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.md,
    fontWeight: theme.typography.fontWeightMedium,
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginRight: theme.spacing(1),
}));

const QuoteStatusChip = styled(Box, {
    shouldForwardProp: (prop) => prop !== '$statuscolor',
})(({ theme, $statuscolor }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: theme.spacing(0.25, 1),
    borderRadius: 12,
    fontSize: theme.typography.size.xs,
    fontWeight: theme.typography.fontWeightSemiBold,
    backgroundColor: $statuscolor || '#757575',
    color: '#fff',
    whiteSpace: 'nowrap',
    flexShrink: 0,
}));

const QuoteDate = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    marginLeft: theme.spacing(1),
    flexShrink: 0,
}));

const EmptyState = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '150px',
    color: theme.palette.text.secondary,
}));

const EmptyStateIcon = styled(Receipt)(({ theme }) => ({
    fontSize: 40,
    marginBottom: theme.spacing(1),
    opacity: 0.5,
}));

const ClientQuotesWidget = ({ widgetId, config = {}, onRemove }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const { data: quotes, isLoading, error } = useFetchQueue(widgetId, {
        apiCall: () => requestGetQuotes({ exclude: 'Accepted,Declined', sort: '-updatedAt', limit: 5 }),
        cacheKey: 'client-quotes-widget',
    });

    const displayQuotes = quotes || [];
    const hasData = displayQuotes.length > 0;

    return (
        <BaseWidget
            title="My Quotes"
            widgetId={widgetId}
            onRemove={onRemove}
            isLoading={isLoading}
            error={error}
        >
            {!hasData ? (
                <EmptyState>
                    <EmptyStateIcon />
                    <Typography variant="body2">No quotes found</Typography>
                </EmptyState>
            ) : (
                <QuoteList>
                    {displayQuotes.map((quote) => (
                        <QuoteRow key={quote._id} onClick={() => navigate(`/my-quotes/${quote._id}`)}>
                            <QuoteName>{quote.name || quote.projectType}</QuoteName>
                            <QuoteStatusChip $statuscolor={statusColor(theme, 'quote', quote.status)}>
                                {quote.status}
                            </QuoteStatusChip>
                            <QuoteDate>{quote.sentAt ? formatDayShort(quote.sentAt) : formatDayShort(serverDayOf(quote.createdAt)) || 'N/A'}</QuoteDate>
                        </QuoteRow>
                    ))}
                </QuoteList>
            )}
        </BaseWidget>
    );
};

export default ClientQuotesWidget;
